import type {
  DomainErrorResponse,
  ErrorDetails,
  IdentityNotVerifiedErrorDetails,
  LoanRejectionErrorDetails,
  MarketplaceErrorCode,
  MarketplaceErrorResponse,
  OAuth2Error,
} from "@/generated/api/types.gen"

export type ApiErrorBody = MarketplaceErrorResponse | DomainErrorResponse | ErrorDetails | OAuth2Error

type MarketplaceErrorBody<C extends MarketplaceErrorCode> = Extract<MarketplaceErrorResponse, { code: C }>

const GENERIC_FAILURE = "Algo salió mal, por favor intenta de nuevo."

/**
 * api-v2 answers with two envelopes: DomainErrorResponse, and an older { message, status } plus one
 * field per subclass that carries no code. [code] is null for the second; [body] is how it is read.
 */
export class ApiError extends Error {
  readonly status: number | null
  readonly code: string | null
  readonly traceId: string | null
  readonly body: unknown

  constructor(init: { status: number | null; code: string | null; message: string; traceId?: string | null; body?: unknown }) {
    super(init.message)
    this.name = "ApiError"
    this.status = init.status
    this.code = init.code
    this.traceId = init.traceId ?? null
    this.body = init.body ?? null
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === "object" && !Array.isArray(value)

const envelopeOf = (body: unknown): Record<string, unknown> | null => (isRecord(body) ? body : null)

const stringOf = (value: unknown): string | null => (typeof value === "string" ? value : null)

const messageOf = (body: unknown): string | null => stringOf(envelopeOf(body)?.message) || null

export const apiErrorFromBody = (status: number, body: unknown): ApiError => {
  const envelope = envelopeOf(body)

  return new ApiError({
    status,
    code: stringOf(envelope?.code) ?? stringOf(envelope?.error),
    message: messageOf(body) ?? stringOf(envelope?.error_description) ?? `Request failed with status code ${status}`,
    traceId: stringOf(envelopeOf(envelope?.context)?.traceId),
    body,
  })
}

const read = (error: unknown): ApiError | null => (error instanceof ApiError ? error : null)

export const errorStatus = (error: unknown): number | null => read(error)?.status ?? null

const MISSING_RESOURCE_STATUSES = new Set([400, 404, 410])

export const isMissingResource = (error: unknown): boolean => MISSING_RESOURCE_STATUSES.has(errorStatus(error) ?? 0)

export const errorCode = (error: unknown): string | null => read(error)?.code ?? null

export const errorBodyMessage = (error: unknown): string | null => messageOf(read(error)?.body ?? null)

export const errorMessage = (error: unknown, fallback: string = GENERIC_FAILURE): string => errorBodyMessage(error) ?? ((error instanceof Error && error.message) || fallback)

const isMarketplaceErrorBody = <C extends MarketplaceErrorCode>(body: unknown, code: C): body is MarketplaceErrorBody<C> => envelopeOf(body)?.code === code

// A marketplace operation's error body, which the spec publishes as a union keyed on `code`: narrow on a code and
// `details` is that code's shape. Narrow only to a marketplace code, since another domain's envelope, with a code of
// its own, can reach the same call.
export const marketplaceErrorBody = <C extends MarketplaceErrorCode>(error: unknown, code: C): MarketplaceErrorBody<C> | null => {
  const body = read(error)?.body
  return isMarketplaceErrorBody(body, code) ? body : null
}

export const errorViolations = (error: unknown): string[] => marketplaceErrorBody(error, "marketplace.feed.page_invalid")?.details.violations ?? []

export const isUnauthorized = (error: unknown): boolean => errorStatus(error) === 401

// 404 is the only outcome of GET /order/payment-options/{checkoutToken} that unambiguously means the
// checkout token has no order (dead/unknown link). The error body carries no stable code, so we
// gate on the status alone; invalid-token (400) / expired (401) need a backend error code to split.
export const isNotFound = (error: unknown): boolean => errorStatus(error) === 404

const errorDetails = (error: unknown): ErrorDetails | null => {
  const body = read(error)?.body
  const hasErrorType = (value: unknown): value is ErrorDetails => typeof envelopeOf(value)?.errorType === "string"
  return hasErrorType(body) ? body : null
}

export const errorType = (error: unknown): ErrorDetails["errorType"] | null => errorDetails(error)?.errorType ?? null

export const loanRejectionReason = (error: unknown): LoanRejectionErrorDetails["loanRejectionReason"] | null => {
  const details = errorDetails(error)
  return details?.errorType === "LOAN_REJECTION" ? (details.loanRejectionReason ?? null) : null
}

export const isLoanDeclined = (error: unknown): boolean => errorDetails(error)?.errorType === "LOAN_REJECTION"

export const isLoanRejection = (error: unknown): boolean => errorStatus(error) === 403 && isLoanDeclined(error)

export const identityNotVerifiedDetails = (error: unknown): IdentityNotVerifiedErrorDetails | null => {
  const details = errorDetails(error)
  return details?.errorType === "IDENTITY_NOT_VERIFIED" ? details : null
}

export const isIdentityNotVerified = (error: unknown): boolean => identityNotVerifiedDetails(error) !== null

// A 4xx (e.g. 403 rejected / 404 not found) is final, so don't burn ~7s retrying it.
export const isRetriable = (error: unknown): boolean => {
  const status = errorStatus(error)
  return status === null || status < 400 || status >= 500
}
